import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import {
  Camera, Check, Loader2, Trash2, AlertTriangle, X, CheckCircle2, Hand,
} from "lucide-react";

/**
 * The phone page a student reaches by scanning the QR code on their sheet.
 *
 * Deliberately outside the login. A fifteen-year-old standing at a kitchen
 * table should not have to type a password to photograph their own working, and
 * the token in the URL is the permission: it belongs to one attempt, grants
 * only "attach a photo to a question in this set", and expires with the
 * assignment.
 *
 * So this uses a bare axios client rather than the shared `api` instance - that
 * one attaches the stored login token, which does not exist here.
 *
 * Two things this page was missing, both reported after the first real use.
 * A student could not see what they had photographed, so a blurred page or a
 * photo filed against the wrong question was invisible until the tutor opened
 * it. And there was no way to say "I have finished": the work sat there
 * looking unstarted. Both are below, and the hand-in says plainly what it
 * locks before it does it.
 */

const client = axios.create({ baseURL: `${BACKEND_URL}/api` });

/** A photo, full size, over the page. Tapping a thumbnail is how a student
 *  checks it is actually readable - a 70px square cannot answer that. */
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/90 flex items-center justify-center p-3"
         onClick={onClose}>
      <button type="button" onClick={onClose}
        className="absolute top-3 right-3 text-white/80 hover:text-white p-2">
        <X size={22} />
      </button>
      <img src={src} alt="Your working, full size"
        className="max-h-full max-w-full rounded-lg"
        onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

function PhotoStrip({ photos, token, locked, onDelete, onOpen }) {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {photos.map((p) => {
        const src = `${BACKEND_URL}/api/upload/${token}/photo/${p.file_id}`;
        return (
          <div key={p.file_id} className="relative">
            <button type="button" onClick={() => onOpen(src)}
              className="block w-[78px] h-[78px] rounded-lg overflow-hidden border border-slate-300 bg-white">
              <img src={src} alt="Your working" className="w-full h-full object-cover" />
            </button>
            {!locked && (
              <button type="button" onClick={() => onDelete(p.file_id)}
                aria-label="Delete this photo"
                className="absolute -top-1.5 -right-1.5 bg-white border border-slate-300 rounded-full p-1 shadow-sm text-red-600 hover:bg-red-50">
                <Trash2 size={12} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function Upload() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [handing, setHanding] = useState(false);
  const [big, setBig] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    client.get(`/upload/${token}`)
      .then((r) => {
        setData(r.data);
        setActive((cur) => cur || (r.data.question_codes || [])[0] || null);
      })
      .catch((e) => setError(e.response?.data?.detail || "That link is not valid."));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pick = () => fileRef.current && fileRef.current.click();

  const send = (file) => {
    if (!file || !active) return;
    setBusy(true);
    setError(null);
    const body = new FormData();
    body.append("question_code", active);
    body.append("file", file);
    client.post(`/upload/${token}`, body)
      .then(() => load())
      .catch((e) => setError(e.response?.data?.detail || "That photo would not upload."))
      .finally(() => {
        setBusy(false);
        if (fileRef.current) fileRef.current.value = "";
      });
  };

  const remove = (fileId) => {
    setError(null);
    client.delete(`/upload/${token}/${fileId}`)
      .then(() => load())
      .catch((e) => setError(e.response?.data?.detail || "Could not delete that photo."));
  };

  const handIn = () => {
    setHanding(true);
    setError(null);
    client.post(`/upload/${token}/submit`)
      .then(() => { setConfirming(false); load(); })
      .catch((e) => {
        // The gate refuses with the questions it is still waiting for. On a
        // phone, in the evening, "could not hand it in" is the end of the
        // road - the list is the whole value of the message.
        const detail = e.response?.data?.detail;
        if (detail && typeof detail === "object") {
          setError(`${detail.error} Still waiting for: ${(detail.missing || []).join(", ")}.`);
          setConfirming(false);
          load();
        } else {
          setError(detail || "Could not hand it in.");
        }
      })
      .finally(() => setHanding(false));
  };

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-sm text-center">
          <AlertTriangle size={26} className="mx-auto text-amber-500 mb-3" />
          <p className="font-semibold text-slate-800">{error}</p>
          <p className="text-sm text-slate-500 mt-2">Ask your tutor for a new link.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    );
  }

  const codes = data.question_codes || [];
  const uploaded = data.uploaded || {};
  const photos = data.photos || {};
  const withWorking = codes.filter((c) => (uploaded[c] || 0) > 0).length;
  const locked = data.status === "submitted";
  // From the server: the same list the hand-in gate refuses on, already
  // allowing for questions declared not attempted on the main site. Worked
  // out here it would eventually disagree with the gate, and the child would
  // be told to photograph something they had already dealt with.
  const noPhoto = data.missing_photos || [];
  const skipped = data.not_attempted || {};
  const timing = data.timing || {};

  return (
    <div className="min-h-screen bg-slate-50 pb-10">
      <div className="max-w-md mx-auto px-4 py-6">
        <p className="text-xs uppercase tracking-wider font-bold text-slate-400">{data.title}</p>
        <h1 className="text-xl font-semibold text-slate-900 mt-1">
          {locked ? "Your work, handed in" : "Upload your working"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {data.student_name} · {withWorking} of {codes.length} questions have a photo
        </p>

        {/* On a timed paper this page is where the last five minutes are
            spent, so it has to say how long is left. */}
        {!locked && timing.expired && timing.upload_seconds_remaining != null && (
          <p className="mt-3 text-sm font-semibold text-amber-900 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2">
            Time is up — {Math.floor(timing.upload_seconds_remaining / 60)}m{" "}
            {timing.upload_seconds_remaining % 60}s left to photograph your working. If you run out
            you will get another five minutes.
          </p>
        )}

        {error && (
          <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
        )}

        {locked && (
          <div className="mt-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex items-start gap-2.5">
            <CheckCircle2 size={17} className="text-green-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm text-green-900">Handed in. Your tutor can see it.</p>
              <p className="text-xs text-green-800 mt-0.5">
                The photos are locked now, so nothing can be added or removed. You can still look at them.
                Sign in on the main site to see the mark scheme.
              </p>
            </div>
          </div>
        )}

        {!locked && (
          <>
            <p className="text-sm font-medium text-slate-700 mt-5 mb-2">Which question is this?</p>
            <div className="flex flex-wrap gap-2">
              {codes.map((c) => {
                const n = uploaded[c] || 0;
                const on = active === c;
                const skip = !!skipped[c];
                return (
                  <button key={c} type="button" onClick={() => setActive(c)}
                    className={`font-mono text-xs rounded-full border px-3 py-2 ${
                      on ? "bg-slate-800 text-white border-slate-800"
                         : n > 0 ? "bg-green-50 border-green-300 text-green-800"
                         : skip ? "bg-slate-100 border-slate-300 text-slate-400 line-through"
                                 : "bg-white border-slate-300 text-slate-700"}`}>
                    {c}{n > 0 ? ` · ${n}` : ""}
                  </button>
                );
              })}
            </div>

            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => send(e.target.files && e.target.files[0])} />

            <button type="button" onClick={pick} disabled={busy || !active}
              className="mt-5 w-full border-2 border-dashed border-slate-300 rounded-xl bg-white py-9 text-center disabled:opacity-50">
              {busy
                ? <Loader2 size={26} className="mx-auto animate-spin text-slate-400" />
                : <Camera size={26} className="mx-auto text-slate-400" />}
              <span className="block font-semibold text-slate-700 mt-2">
                {busy ? "Uploading…" : "Take a photo"}
              </span>
              <span className="block text-sm text-slate-500 mt-0.5">
                {active ? `for ${active}` : "pick a question first"}
              </span>
            </button>

            {/* The photos just taken for the question in hand, right under the
                camera button - so checking the last one is one glance, not a
                scroll to the bottom of the page. */}
            {active && (photos[active] || []).length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-500">
                  Your photos for <span className="font-mono">{active}</span> — tap one to see it properly
                </p>
                <PhotoStrip photos={photos[active]} token={token} locked={locked}
                  onDelete={remove} onOpen={setBig} />
              </div>
            )}

            <p className="text-xs text-slate-400 mt-4">
              Photograph each page of your working. You can add more than one photo per question, and take
              them in any order. Check each photo is readable before you hand in — nothing is marked from a
              photo automatically, your tutor reads them.
            </p>
          </>
        )}

        {withWorking > 0 && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">
              Everything you have uploaded
            </p>
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              {codes.filter((c) => (uploaded[c] || 0) > 0).map((c) => (
                <div key={c} className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Check size={15} className="text-green-600 shrink-0" />
                    <span className="font-mono text-xs flex-1">{c}</span>
                    <span className="text-xs text-slate-500">
                      {uploaded[c]} photo{uploaded[c] === 1 ? "" : "s"}
                    </span>
                  </div>
                  <PhotoStrip photos={photos[c]} token={token} locked={locked}
                    onDelete={remove} onOpen={setBig} />
                </div>
              ))}
            </div>
          </div>
        )}

        {!locked && (
          <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4">
            {!confirming ? (
              <>
                <p className="font-semibold text-sm text-slate-800">Finished?</p>
                <p className="text-xs text-slate-500 mt-1">
                  {noPhoto.length === 0
                    ? "Every question has a photo, or is marked as not attempted."
                    : `${noPhoto.length} question${noPhoto.length === 1 ? " has" : "s have"} no photo yet: ${noPhoto.join(", ")}.`}
                </p>
                {noPhoto.length > 0 && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mt-2">
                    Photograph {noPhoto.length === 1 ? "it" : "them"} before you hand in. If you did not do
                    one at all, open the assignment on the main site and mark it as not attempted.
                  </p>
                )}
                <button type="button" onClick={() => setConfirming(true)} disabled={noPhoto.length > 0}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-slate-800 text-white font-semibold rounded-lg py-3 disabled:opacity-40">
                  <Hand size={16} /> Hand in my work
                </button>
              </>
            ) : (
              <>
                <p className="font-semibold text-sm text-slate-800">Hand in {data.title}?</p>
                <ul className="text-xs text-slate-600 mt-2 space-y-1 list-disc pl-4">
                  <li>
                    {withWorking} of {codes.length} question{codes.length === 1 ? "" : "s"} have a photo
                    {noPhoto.length > 0 ? ` — nothing for ${noPhoto.join(", ")}` : ""}
                  </li>
                  <li>
                    You have typed {data.answers_typed} of {codes.length} answers on the main site
                  </li>
                  <li>After this you cannot add photos or change your answers</li>
                  <li>The mark scheme unlocks once you have handed in</li>
                </ul>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={handIn} disabled={handing}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-800 text-white font-semibold rounded-lg py-3 disabled:opacity-50">
                    {handing ? <Loader2 size={16} className="animate-spin" /> : <Hand size={16} />}
                    {handing ? "Handing in…" : "Yes, hand it in"}
                  </button>
                  <button type="button" onClick={() => setConfirming(false)}
                    className="px-4 text-sm text-slate-600 border border-slate-300 rounded-lg">
                    Not yet
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-8">
          {locked
            ? "You can close this page."
            : "You can close this page and come back to it from the same QR code."}
        </p>
      </div>

      {big && <Lightbox src={big} onClose={() => setBig(null)} />}
    </div>
  );
}
